// @ts-strict-ignore
import { ArticleBehaviorPropertyDef } from '@article/constants/article-behavior-property-def';
import { ArticleVO } from '@article/model/vo/article.vo';
import { BaseMultipleConnectionConnectProcessor } from './base-multiple-connection-connect.processor';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ConnectionAreaVO, MetaElementUsageIdentityVO, MetaElementUsageVO } from '@shared/vo/meta-element.vo';
import { Connection } from '@3d-scene/model3d/connection';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { MetaElementUsageUtils } from '@project/util/meta-element-usage-utils';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleUtils } from '@article/util/article-utils';
import { BridgeArticleManager } from '../../../manager/bridge-article.manager';
import { UndoBreakPinsOperation } from '@shared/undo/operation/undo-break-pins-operation';
import { Injectable } from '@angular/core';
import { ArticleContainerFactory } from '@3d-scene/model3d/article-container.factory';
import { ArticleManager } from '@article/manager/article.manager';
import { CompositeArticleCacheManager } from '@article/manager/composite-article-cache.manager';
import { ArticleContainerLoader } from '@article/service/article-container-loader';
import { CompositeArticleUtils } from '@article/util/composite-article-utils';
import { ProjectModel } from '@project/model/project.model';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ProjectModelService } from '@project/service/project-model.service';
import { ProjectArticleService } from '@project/service/project-article.service';
import { ProjectMetaElementService } from '@project/manager/project-meta-element.service';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';
import { CompositeArticleInfoUtils } from '@article/util/composite-article-info-utils';
import { PropertyUtils } from '@article/util/property-utils';

@Injectable({ providedIn: 'root' })
export class BridgeArticleConnectProcessor extends BaseMultipleConnectionConnectProcessor {
  public collisionCheckRequired(article: ArticleVO): boolean {
    return !PropertyUtils.getIntPropertyValue(article.behaviorProperties, ArticleBehaviorPropertyDef.IS_CONTINUOUS_JUMPER);
  }

  constructor(
    projectModel: ProjectModel,
    projectModelService: ProjectModelService,
    projectArticleService: ProjectArticleService,
    projectMetaElementService: ProjectMetaElementService,
    localeResourceManager: LocaleResourceManager,
    articleContainerLoader: ArticleContainerLoader,
    compositeArticleCacheManager: CompositeArticleCacheManager,
    articleContainerFactory: ArticleContainerFactory,
    articleManager: ArticleManager,
    compositeArticleUtils: CompositeArticleUtils,
    compositeArticleInfoUtils: CompositeArticleInfoUtils,
    articleContainerUtils: ArticleContainerUtils,
    connectionInfoService: ConnectionInfoService,
    containerService: ContainerService,
    detachArticleUsageService: DetachArticleUsageService,
    private bridgeManager: BridgeArticleManager,
  ) {
    super(
      projectModel,
      projectModelService,
      projectArticleService,
      projectMetaElementService,
      localeResourceManager,
      articleContainerLoader,
      compositeArticleCacheManager,
      articleContainerFactory,
      articleManager,
      compositeArticleUtils,
      compositeArticleInfoUtils,
      connectionInfoService,
      containerService,
      articleContainerUtils,
      detachArticleUsageService,
    );
  }

  public process(
    containers: ArticleContainer[],
    baseContainer: ArticleContainer,
    givingConnectionAreas: ConnectionAreaVO[],
    rotationAngle: number
  ): void {
    super.process(containers, baseContainer, givingConnectionAreas, rotationAngle);
    this.breakUnusedConnections(baseContainer);
  }

  protected createMetaElementUsages(
    givingArticleContainers: ArticleContainer[],
    givingConnectionCubes: Connection[],
    acceptingConnectionCubes: Connection[]
  ): void {
    if (ArticleContainerUtils.isStaggeredJumperArticleContainers(givingArticleContainers)) {
      this.breakStaggeredJumperPins(givingConnectionCubes, acceptingConnectionCubes);
    }
    super.createMetaElementUsages(givingArticleContainers, givingConnectionCubes, acceptingConnectionCubes);
  }

  private breakStaggeredJumperPins(givingConnectionCubes: Connection[], acceptingConnectionCubes: Connection[]): void {
    const sourceGivingConnections: Connection[] = givingConnectionCubes.concat();
    const sourceAcceptingConnections: Connection[] = acceptingConnectionCubes.concat();
    const deletedConnectedConnections: Connection[] = [];

    let currentGivingCube: Connection;
    let currentAcceptingCube: Connection;
    for (let i: number = sourceAcceptingConnections.length - 1; i >= 0; i--) {
      currentAcceptingCube = sourceAcceptingConnections[i];
      currentGivingCube = sourceGivingConnections[i];
      const connectionArea: MetaElementUsageVO = currentAcceptingCube.metaElementUsage;
      const connectedConnectionIds: MetaElementUsageIdentityVO[] = MetaElementUsageUtils.forwardConnectedMetaElementUsageIdentities(connectionArea);
      if (connectedConnectionIds.length === 0) {
        continue;
      }
      if (!ConnectionInfoUtils.hasDynamicPinReference(currentGivingCube.metaElementVO)) {
        for (const metaElementIdentity of connectedConnectionIds) {
          const connectedArticleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(metaElementIdentity.articleUsageId);
          if (!ArticleUtils.isStaggeredJumperArticle(connectedArticleContainer.article)) {
            continue;
          }
          const connectedConnectionCube: Connection = connectedArticleContainer.findFirstConnectionByName(metaElementIdentity.metaElementName);
          if (ConnectionInfoUtils.hasDynamicPinReference(connectedConnectionCube.metaElementVO)) {
            deletedConnectedConnections.push(connectedConnectionCube);
          } else {
            throw new Error(this.localeResourceManager.getValueByKey('connectProcessor.message.staggered_jumper_not_suitable'));
          }
        }
      } else {
        givingConnectionCubes.splice(i, 1);
        acceptingConnectionCubes.splice(i, 1);
      }
    }
    if (deletedConnectedConnections.length > 0) {
      this.bridgeManager.breakPins(deletedConnectedConnections);

      const breakUndoOperation: UndoBreakPinsOperation = new UndoBreakPinsOperation(
        deletedConnectedConnections,
        this.bridgeManager,
        this.containerService,
        this.connectionInfoService
      );
      this._createdUndoOperations = [breakUndoOperation];
    }
  }

  private breakUnusedConnections(baseContainer: ArticleContainer): void {
    const freeGivingAreas: ConnectionAreaVO[] = ConnectionInfoUtils.getFreeConnectionAreas(baseContainer, ConnectionAreaVO.SIDE_GIVING);
    const connectionCubes: Connection[] = [];
    for (const freeGivingArea of freeGivingAreas) {
      const connectionCube: Connection = baseContainer.findFirstConnectionByName(freeGivingArea.name);
      if (connectionCube) {
        connectionCubes.push(connectionCube);
      }
    }
    if (connectionCubes.length > 0) {
      this.bridgeManager.breakPins(connectionCubes);
    }
  }
}
